/* eslint-disable import/prefer-default-export, no-param-reassign */
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import { sleep } from "common/utils/helpers/delay";
import { track as mixpanelTrack } from "jiffy-analytics";
import { SignInModalUtils } from "./sign-in-modal-utils";
import { RecaptchaHandler } from "./recaptcha-handler";

export class SignInModalFormRequestClass {
  #props = {};

  #modalSelector = ".js-sign-in-modal";

  #recaptchaHandler = null;

  #countdownInterval = null;

  #countdownActive = false;

  #originalButtonText = "";

  constructor(props) {
    this.#props = props;
  }

  get #$modal() {
    return document.querySelector(this.#modalSelector);
  }

  get #$form() {
    return this.#$modal.querySelector(this.#props.formSelector);
  }

  get #$error() {
    return this.#$modal.querySelector(this.#props.errorSelector);
  }

  get #$submitButton() {
    return this.#$form.querySelector('[type="submit"]');
  }

  get #$rememberMeCheckbox() {
    return this.#$form.querySelector('[type="checkbox"]');
  }

  hideErrors() {
    this.#$error.classList.add("hidden");
  }

  hideRecaptcha() {
    this.#recaptchaHandler?.hide?.();
  }

  showRecaptcha() {
    this.#recaptchaHandler?.show?.();
  }

  handleTwoFactorAuth(data) {
    SignInModalUtils.handleTwoFactorAuth(data, this.#$form, this.#$modal);
  }

  #startCountdown(seconds) {
    this.#stopCountdown();
    this.#countdownActive = true;

    if (!this.#originalButtonText) {
      this.#originalButtonText = this.#$submitButton.textContent.trim();
    }

    sleep(100).then(() => {
      this.#$submitButton.disabled = true;
      if (this.#recaptchaHandler) {
        this.#recaptchaHandler.setCountdownState(true);
      }

      let remainingSeconds = seconds;

      const updateButton = () => {
        this.#$submitButton.textContent = `Please wait (${remainingSeconds})`;

        if (remainingSeconds <= 0) {
          this.#stopCountdown();
        } else {
          remainingSeconds -= 1;
        }
      };

      updateButton();

      this.#countdownInterval = setInterval(updateButton, 1000);
    });
  }

  #stopCountdown() {
    if (this.#countdownInterval) {
      clearInterval(this.#countdownInterval);
      this.#countdownInterval = null;
    }

    this.#countdownActive = false;

    if (this.#originalButtonText && this.#$submitButton) {
      this.#$submitButton.textContent = this.#originalButtonText;
    }

    if (this.#recaptchaHandler) {
      this.#recaptchaHandler.setCountdownState(false);

      if (this.#recaptchaHandler.isVisible()) {
        this.#recaptchaHandler.ensureButtonState();
      } else {
        this.#recaptchaHandler.enableFormElements();
      }
    } else {
      this.#$submitButton.disabled = false;
    }
  }

  init() {
    this.#setupFormAttributes();
    this.#addDesignProjectUuidField();
    this.#setupEventListeners();

    // Only create RecaptchaHandler for sign-in form, not sign-up form
    if (this.#props.mixpanelName === "customer_logged_in") {
      this.#recaptchaHandler = new RecaptchaHandler({
        containerSelector: ".js-sign-in-modal .js-sign-in-modal-recaptcha-container",
        recaptchaElementId: "sign-in-modal-recaptcha",
        submitButton: this.#$submitButton,
        rememberMeCheckbox: this.#$rememberMeCheckbox
      });
    }
  }

  #addDesignProjectUuidField() {
    if (!this.#props.uuid) return;
    // Check if the field already exists to avoid duplicates
    const existingField = this.#$form.querySelector('input[name="design_project_uuid"]');
    if (existingField) return;
    const hiddenInput = document.createElement("input");
    hiddenInput.type = "hidden";
    hiddenInput.name = "design_project_uuid";
    hiddenInput.value = this.#props.uuid;
    this.#$form.insertBefore(hiddenInput, this.#$form.firstChild);
  }

  #setupFormAttributes() {
    $(this.#$form).on("ajax:beforeSend", (event, xhr) => {
      xhr.setRequestHeader("Accept", "application/json, text/javascript, */*; q=0.01");
    });
  }

  #setupEventListeners() {
    $(this.#$modal).on("hidden.bs.modal", () => {
      this.hideErrors();
      this.hideRecaptcha();
      this.#stopCountdown();
      this.#$form.reset();
    });
    $(this.#$form).on({
      "ajax:before": event => {
        if (this.#countdownActive) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return false;
        }
        this.#$error.classList.add("hidden");
        return true;
      },
      "ajax:success": (_, data) => this.#handleAjaxSuccess(data),
      "ajax:error": (_event, error) => this.#handleAjaxError(error),
      "ajax:complete": () => {
        if (!this.#countdownActive) {
          this.#recaptchaHandler?.ensureButtonState?.();
        }
      }
    });
  }

  #handleAjaxSuccess(data) {
    const inputType = SignInModalFormRequestClass.detectInputType(data);

    // Handle non-JSON response by executing as script
    if (inputType !== "json") {
      const script = document.createElement("script");
      script.textContent = data;
      document.body.appendChild(script);
      script.remove();
      return;
    }

    // Handle JSON response
    const fetchedData = objNamesToCamelCase(JSON.parse(data || "{}"));

    // Handle two-factor authentication
    if (fetchedData.requiresTwoFactor) {
      this.handleTwoFactorAuth(fetchedData);
      return;
    }

    // Handle mixpanel event for successful login/sign-up
    mixpanelTrack(this.#props.mixpanelName);

    if (fetchedData.redirectUrl) {
      $(this.#$modal).modal("hide");
      window.location.href = fetchedData.redirectUrl;
    }
  }

  #handleAjaxError(error) {
    let errorData;

    try {
      errorData = JSON.parse(error.responseText);
    } catch (parseError) {
      errorData = { error_message: "An error occurred. Please try again.", show_recaptcha: false };
    }

    this.#$error.classList.remove("hidden");
    this.#$error.innerHTML = SignInModalUtils.replaceLoginLinkWithButton(errorData.error_message);

    // if login delay is a number, and is greater than 0, then start the countdown
    if (Number(errorData.login_delay) > 0) {
      this.#startCountdown(errorData.login_delay);
    }

    if (errorData.show_recaptcha) {
      this.showRecaptcha();
    } else {
      this.hideRecaptcha();
    }
  }

  static detectInputType(input) {
    if (typeof input !== "string") return "unknown";

    const trimmedInput = input.trim();
    if (!trimmedInput) return "unknown";

    try {
      JSON.parse(trimmedInput);
      return "json";
    } catch {
      return trimmedInput.includes("window.location.href") ? "code" : "unknown";
    }
  }
}
